import React from 'react';
import { Award, Briefcase, GraduationCap, Heart, Target, Users, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const AboutPage = ({ language }) => {
  const translations = {
    de: {
      title: 'Über mich',
      subtitle: 'Felix Martinez - Ihr IT-Partner mit Erfahrung und Leidenschaft',
      intro: {
        title: 'Persönlich, kompetent, regional',
        text: 'Mit 15 Jahren Berufserfahrung als Fachinformatiker unterstütze ich Privatkunden und Unternehmen in unserer Region bei allen IT-Themen – verständlich, zuverlässig und auf Augenhöhe.'
      },
      motivation: {
        title: 'Warum ich tue, was ich tue',
        items: [
          {
            icon: Heart,
            title: 'Persönliche Betreuung',
            description: 'Viele Menschen wünschen sich verständliche, persönliche Unterstützung statt anonymen Hotlines oder unpersönlichen Chat‑Bots. Gerade bei IT‑Themen entstehen oft Unsicherheit und Frustration, wenn komplexe Zusammenhänge nicht verständlich erklärt werden. Genau hier setze ich an: Mit meiner langjährigen Erfahrung möchte ich Ihnen in meiner Region konkrete, individuelle Hilfe bei Ihren IT‑Problemen bieten. Ich begleite Sie Schritt für Schritt, nehme mir Zeit für Ihre Fragen und erkläre die Lösungen so, dass Sie sie nachvollziehen und im Alltag selbstständig anwenden können. Ziel ist eine vertrauensvolle Zusammenarbeit, in der Sie sich gut aufgehoben und informiert fühlen.'
          },
          {
            icon: Target,
            title: 'Regionale Nähe',
            description: 'Als lokaler IT‑Dienstleister bin ich schnell vor Ort und kenne die Bedürfnisse von Privatkunden und Unternehmen in unserer Region aus erster Hand. Durch meine regionale Ausrichtung kann ich kurzfristig und flexibel reagieren, ob bei akuten Störungen, geplanten Wartungen oder neuen Projekten. Ich optimiere Abläufe, installiere und konfiguriere Systeme und stehe Ihnen für Fragen und Nachbetreuung direkt zur Verfügung – ohne lange Wartezeiten auf externe Supportstellen. Die Nähe zu Ihnen bedeutet für mich verlässliche Ansprechbarkeit, persönlichen Kontakt und langfristige Zusammenarbeit auf Augenhöhe.'
          },
          {
            icon: Users,
            title: 'Flexibilität & Zuverlässigkeit',
            description: 'Durch meine Tätigkeit im Nebenerwerb kann ich flexibel auf Ihre Anforderungen eingehen und meine Dienstleistungen gezielt an Ihre Bedürfnisse anpassen. Gleichzeitig stehe ich Ihnen mit hoher Zuverlässigkeit und Professionalität zur Seite. Da meine Arbeit aktuell nebenberuflich erfolgt, kann es vereinzelt zu zeitlichen Verzögerungen oder begrenzten Kapazitäten kommen. Termine und Aufträge plane ich daher sorgfältig, um trotzdem einen zuverlässigen Service zu gewährleisten.'
          }
        ]
      },
      qualification: {
        title: 'Qualifikation & Erfahrung',
        items: [
          {
            icon: GraduationCap,
            title: 'Fundierte Ausbildung',
            description: 'IHK-anerkannter Fachinformatiker für Systemintegration mit umfassenden Kenntnissen in Kundenberatung, Kalkulation, Angebotserstellung und Projektabwicklung.'
          },
          {
            icon: Briefcase,
            title: '15 Jahre Berufserfahrung',
            description: 'Seit 2011 in der IT tätig (Stand 2026), davon etwa 4 Jahre direkt bei einem IT-Dienstleister. Umfassende Praxis in Computer-Systemen, Netzwerken, Cloud-Technologien und Kundenbetreuung.'
          },
          {
            icon: Award,
            title: 'Kaufmännische Kompetenz',
            description: 'Durch Ausbildung und Praxis versiert in Bedarfsanalyse, Vertragsgestaltung, Rechnungsstellung, Terminplanung und allen kaufmännischen Aspekten der IT-Dienstleistung.'
          }
        ]
      },
      approach: {
        title: 'Mein Ansatz',
        text: 'IT-Probleme können frustrierend sein – ich sorge dafür, dass sie es nicht bleiben. Mit verständlichen Erklärungen, transparenten Abläufen und persönlichem Engagement finde ich Lösungen, die zu Ihnen passen und langfristig funktionieren.',
        values: [
          'Verständliche Kommunikation statt Fachchinesisch',
          'Transparente Preise und Abläufe',
          'Schnelle Reaktionszeiten und Termintreue',
          'Langfristige Betreuung statt Einmalaktionen',
          'Persönlicher Ansprechpartner für alle IT-Fragen'
        ]
      },
      cta: {
        title: 'Lassen Sie uns sprechen',
        text: 'Ob Windows 11 Umstieg, PC-Einrichtung oder Netzwerkprobleme – ich freue mich darauf, Sie kennenzulernen und gemeinsam Lösungen zu finden.',
        button: 'Jetzt Kontakt aufnehmen'
      }
    },
    en: {
      title: 'About Me',
      subtitle: 'Felix Martinez - Your IT partner with experience and passion',
      intro: {
        title: 'Personal, competent, regional',
        text: 'With 15 years of professional experience as an IT specialist, I support private customers and small businesses in our region with all IT issues – understandably, reliably and at eye level.'
      },
      motivation: {
        title: 'Why I Do What I Do',
        items: [
          {
            icon: Heart,
            title: 'Personal Care',
            description: 'Many people are looking for clear, personal support rather than anonymous hotlines or impersonal chatbots. Especially when it comes to IT issues, uncertainty and frustration often arise if technical details are not explained in an understandable way. This is exactly where I come in: With my many years of experience, I want to provide concrete and individual help with your IT problems in my local area. I will guiding you step by step, taking the time to answer your questions and explaining solutions in a way that you can follow and confidently apply in your daily life. My goal is a trusting collaboration in which you feel well taken care of and properly informed.'
          },
          {
            icon: Target,
            title: 'Regional Proximity',
            description: 'As a local IT service provider, I can be on site quickly and know the needs of private customers and small businesses in our region from firsthand experience. My regional focus allows me to react promptly and flexibly, whether it comes to urgent issues, scheduled maintenance, or new projects. I optimize workflows, install and configure systems, and remain available for follow‑up questions and support—without long waiting times for external support teams. Being close to you means reliable availability, personal contact, and long‑term cooperation on an equal footing.'
          },
          {
            icon: Users,
            title: 'Flexibility & Reliability',
            description: 'Due to my work in a secondary occupation, I can respond flexibly to your requirements and tailor my services precisely to your needs. At the same time, I provide you with a highly reliable and professional level of support. As my work is currently conducted on a part‑time basis, there may occasionally be delays or limited availability. I therefore plan appointments and assignments carefully to ensure that I can still offer a dependable service.'
          }
        ]
      },
      qualification: {
        title: 'Qualification & Experience',
        items: [
          {
            icon: GraduationCap,
            title: 'Sound Training',
            description: 'Chamber of Commerce certified IT specialist for system integration with comprehensive knowledge in customer consulting, calculation, offer preparation and project management.'
          },
          {
            icon: Briefcase,
            title: '15 Years of Professional Experience',
            description: 'Active in IT since 2011 (as of 2026), including about 4 years directly at an IT service provider. Comprehensive practice in computer systems, networks, cloud technologies and customer support.'
          },
          {
            icon: Award,
            title: 'Commercial Competence',
            description: 'Through training and practice, well-versed in needs analysis, contract design, invoicing, scheduling and all commercial aspects of IT services.'
          }
        ]
      },
      approach: {
        title: 'My Approach',
        text: 'IT problems can be frustrating – I make sure they don\'t stay that way. With understandable explanations, transparent processes and personal commitment, I find solutions that suit you and work long-term.',
        values: [
          'Understandable communication instead of technical jargon',
          'Transparent prices and processes',
          'Fast response times and punctuality',
          'Long-term support instead of one-time actions',
          'Personal contact for all IT questions'
        ]
      },
      cta: {
        title: 'Let\'s Talk',
        text: 'Whether Windows 11 migration, PC setup or network problems – I look forward to getting to know you and finding solutions together.',
        button: 'Contact Now'
      }
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-4" style={{ color: '#E53D4A' }}>
              {t.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {t.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Intro with Image */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <img
              src="/images/felix_martinez.jpg"
              alt="Felix Martinez"
              className="object-cover"
              />
              {/*
              <div className="order-2 md:order-1">
                <div className="aspect-square rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-[#E53D4A] flex items-center justify-center">
                      <Users className="w-16 h-16 text-white" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 italic">
                      {language === 'de' ? 'Platzhalter für Ihr Foto' : 'Placeholder for your photo'}
                    </p>
                  </div>
                </div>
              </div>*/}

              <div className="order-1 md:order-2">
                <h2 className="text-3xl font-bold mb-4" style={{ color: '#E53D4A' }}>
                  {t.intro.title}
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t.intro.text}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Motivation */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#E53D4A' }}>
              {t.motivation.title}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {t.motivation.items.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                    <CardContent className="pt-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-[#E53D4A] flex items-center justify-center mb-4">
                          <IconComponent className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold mb-3" style={{ color: '#E53D4A' }}>
                          {item.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Qualification */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#E53D4A' }}>
              {t.qualification.title}
            </h2>
            <div className="space-y-6">
              {t.qualification.items.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#E53D4A] flex items-center justify-center">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#E53D4A' }}>
                        {item.title}
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-6" style={{ color: '#E53D4A' }}>
              {t.approach.title}
            </h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 text-center mb-8 leading-relaxed">
              {t.approach.text}
            </p>
            <Card className="bg-white dark:bg-gray-900">
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {t.approach.values.map((value, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{value}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#E53D4A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t.cta.title}
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              {t.cta.text}
            </p>
            <Button 
              size="lg" 
              className="bg-white text-[#E53D4A] hover:bg-gray-100"
              asChild
            >
              <a href="mailto:kontakt@martinez.systems">
                {t.cta.button}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
